import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Login.css";
import { toast } from "react-toastify";
import { getBrowserInfo, getUserIp, getHeaders } from "./api/client";

const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountlocked, setAccountLocked] = useState(false);
  const [isDefaultPassword, setIsDefaultPassword] = useState(false);

  useEffect(() => {
    getUserIp();
    getBrowserInfo();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headers = getHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/api/auth/login`,
        { ...form },
        { headers: { "Content-Type": "application/json", ...headers } }
      );
      const rawRole = response.data.role?.toLowerCase() || "";
      const normalizedRole = rawRole === "devread" ? "dev" : rawRole;
      sessionStorage.setItem("role", normalizedRole);
      if (response.data.success) {
        const { token, roleId, role, userId, email, username, isAccountLocked, isDefaultPassword: defaultPwdFlag } = response.data;
        
        sessionStorage.setItem("userRoleId", roleId || "");
        sessionStorage.setItem("userId", userId);
        sessionStorage.setItem("userEmail", email);
        sessionStorage.setItem("username", username);
        setAccountLocked(isAccountLocked ?? false);
        if (isAccountLocked) {
          toast.error("Your account is locked due to multiple failed validation attempts. Please try again later.");
          return;
        }

        if (defaultPwdFlag === true) {
          toast.warning("Your account is still using the default password. Please change it immediately for security.", {
            duration: 8000,
            icon: '⚠️',
          });
        }

        navigate("/dashboard");
      } else {
        toast.error("Login failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root" style={{ minHeight: '100dvh' }}>
      {/* LEFT PANEL */}
      <div className="login-left">
        <div className="left-content">
          <h1 className="left-brand">GREEN<span>BILLS</span></h1>
          <h6 className="title">Admin Panel</h6>

          <div className="left-features">
            <div className="feature-item">
              <div className="feature-icon">🛡️</div>
              <div>
                <div className="feature-title">Robust Security</div>
                <div className="feature-desc">Advanced protection, permissions, and logging for secure internal operations</div>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔒</div>
              <div>
                <div className="feature-title">Secure by Design</div>
                <div className="feature-desc">Role-based access and enterprise-grade protection</div>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div>
                <div className="feature-title">Smart Analytics</div>
                <div className="feature-desc">Real-time insights at your fingertips</div>
              </div>
            </div>
          </div>

          {/* decorative circles */}
          <div className="deco-circle c1"></div>
          <div className="deco-circle c2"></div>
          <div className="deco-circle c3"></div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="login-right">
        <div className="login-card">
          <div className="card-header">
            <h2 className="card-title" style={{ 
              fontSize: '38px', 
              fontWeight: 700,
              letterSpacing: '-1.8px',
              marginBottom: '6px',
              lineHeight: 1.05
            }}>Welcome</h2>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="field-group">
              <label className="field-label">Username</label>
              <div className="field-wrap">
                <span className="field-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <input
                  type="text"
                  name="username"
                  placeholder="Enter your username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="field-input"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Password</label>
              <div className="field-wrap">
                <span className="field-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="field-input"
                  autoComplete="current-password"
                />
                <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div className="forgot-row">
              <a 
                href="#" 
                className="forgot-link"
                onClick={(e) => { e.preventDefault(); navigate("/ForgotPassword"); }}
              >
                Forgot password?
              </a>
            </div>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner"></span>
                  Signing in...
                </span>
              ) : (
                <span>Sign In →</span>
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;